/**
 * Phase helpers for Curation Duels (docs/curation-duels.md § Lifecycle).
 *
 * A duel moves open → voting → ended; the API emits `submit_end` (claims lock,
 * voting begins) and `vote_end` (voting closes). These helpers centralize
 * "which deadline is this phase counting down to" and the display names so
 * every surface (cards, detail header, results) agrees.
 */

/** The minimal slice of `Duel` the phase helpers need. */
export interface DuelPhaseFields {
  status: string;
  submit_end: string;
  vote_end: string;
}

/**
 * The ISO deadline the current phase counts down to:
 * open → `submit_end`, voting → `vote_end`, ended (or unknown) → null.
 */
export function phaseEnd(duel: DuelPhaseFields): string | null {
  if (duel.status === 'open') return duel.submit_end;
  if (duel.status === 'voting') return duel.vote_end;
  return null;
}

/** Human label for what the phase countdown means. */
export function phaseLabel(duel: DuelPhaseFields): string {
  if (duel.status === 'open') return 'Claims close';
  if (duel.status === 'voting') return 'Voting ends';
  if (duel.status === 'ended') return 'Ended';
  return '';
}

/** Product display names for the four vote modes (enum → label). */
const MODE_LABELS: Record<string, string> = {
  classic: 'Classic',
  league: 'League',
  rapid: 'Rapid',
  bracket: 'Bracket',
};

/**
 * `vote_mode` enum → display name (Classic / League / Rapid / Bracket).
 * Unknown values fall back to a capitalized echo so nothing renders blank.
 */
export function modeLabel(voteMode: string): string {
  if (voteMode in MODE_LABELS) return MODE_LABELS[voteMode];
  return voteMode ? voteMode.charAt(0).toUpperCase() + voteMode.slice(1) : '';
}
